import bindToForm from '../form/form-protection';
import { htmlTemplate as htmlErrorTemplate, htmlTemplateWizard as wizardErrorTemplate } from './components/modal/internal-error-template';

const loadingSpinnerHTML = `
<svg aria-hidden="true" role="status" class="mr-3 inline h-4 w-4 animate-spin text-white" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9913 9.08144 50.5908Z" fill="#E5E7EB"/>
    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
</svg>
`;

export default class AjaxModal {
    static #instances = new Map();

    #formProtection = null;

    constructor(modalElement, dependencies = {}) {
        this.modal = modalElement;
        this.id = modalElement.id;

        this.window = dependencies.window || window;
        this.document = dependencies.document || document;
        this.fetch = dependencies.fetch || window.fetch.bind(window);
        this.confirm = dependencies.confirm || window.confirm.bind(window);
        this.bindToForm = dependencies.bindToForm || bindToForm;

        AjaxModal.#instances.set(this.id, this);
    }

    static #getInstance(modalId) {
        return AjaxModal.#instances.get(modalId);
    }

    static initialize(modalElements) {
        for (const modal of modalElements) {
            new AjaxModal(modal);
        }

        document.addEventListener('click', AjaxModal.#handleClick);
        document.addEventListener('submit', AjaxModal.#handleSubmit);
    }

    async loadContent(href) {
        this.#showLoadingState();

        try {
            const response = await this.fetch(href, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (response.status === 500) {
                this.#updateModalContent(htmlErrorTemplate, href);
                throw new Error('Unexpected response status: 500');
            }

            const text = await response.text();
            this.#updateModalContent(text, href);
        } catch (error) {
            console.error(error);
        }
    }

    async loadContentViaPost(href, formData) {
        this.#showLoadingState();

        try {
            const response = await this.fetch(href, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (response.status >= 500 && response.status < 600) {
                this.#updateModalContent(htmlErrorTemplate, href);
                throw new Error(`Unexpected response status: ${response.status}`);
            }

            const text = await response.text();
            this.#updateModalContent(text, href);
        } catch (error) {
            console.error(error);
        }
    }

    #showLoadingState() {
        const body = this.modal.querySelector('[data-ajax-modal-body]');
        const header = this.modal.querySelector('[data-ajax-modal-header]');

        body.innerHTML =
            '<div class="flex items-center justify-center p-12"><div class="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div></div>';
        header.innerText = '';
    }

    close() {
        if (this.#shouldPreventClose()) {
            return false;
        }

        const body = this.modal.querySelector('[data-ajax-modal-body]');
        const header = this.modal.querySelector('[data-ajax-modal-header]');

        body.dispatchEvent(new Event('unload'));
        body.innerHTML = '';
        header.innerText = '';

        this.#formProtection = null;
        return true;
    }

    getFormProtection() {
        return this.#formProtection;
    }

    #hasUnsavedChanges() {
        return this.#formProtection !== null && !this.#formProtection.clean();
    }

    #shouldPreventClose() {
        if (!this.#hasUnsavedChanges()) {
            return false;
        }

        return !this.confirm('Er zijn nog niet opgeslagen wijzigingen, weet je zeker dat je dit venster wilt sluiten?');
    }

    #getSubmitButtons(submitter) {
        return Array.from([submitter, this.document.activeElement]).filter((elem) => elem instanceof HTMLButtonElement);
    }

    #getTargetURL(response, formAction) {
        const currentURL = new URL(this.window.location);
        let targetURL = new URL(response.url, this.window.location.origin);

        if (response.status === 201 && response.headers.has('Location')) {
            targetURL = new URL(response.headers.get('Location'), this.window.location.origin);
        }

        if (currentURL.hash.indexOf('#tab=') > -1) {
            targetURL.searchParams.set('tab', currentURL.hash.replace('#tab=', ''));
        }

        // A fetch response URL never carries a fragment, so take it from the returnTo instead.
        if (targetURL.hash === '') {
            const returnTo = new URL(formAction, this.window.location.origin).searchParams.get('returnTo');
            if (returnTo !== null) {
                targetURL.hash = new URL(returnTo, this.window.location.origin).hash;
            }
        }

        return targetURL;
    }

    #shouldReloadOrRedirect(targetURL, response) {
        if (response.status !== 200 && response.status !== 201) {
            return false;
        }

        const currentURL = new URL(this.window.location.href);
        const samePath = targetURL.pathname === currentURL.pathname;
        const sameQuery = targetURL.searchParams.toString() === currentURL.searchParams.toString();

        return samePath && sameQuery;
    }

    #navigateToURL(targetURL, response) {
        if (this.#shouldReloadOrRedirect(targetURL, response)) {
            this.window.location.replace(targetURL.toString());

            // Path and query are equal, so the replace above only jumped to the anchor.
            if (targetURL.hash !== '') {
                this.window.location.reload();
            }
            return;
        }

        this.window.location.href = targetURL.toString();
    }

    #handleFetchError(response, href) {
        const errorTemplate = response.status === 500 ? htmlErrorTemplate : wizardErrorTemplate;

        this.#updateModalContent(errorTemplate, href);
        throw new Error(`Unexpected response status: ${response.status}`);
    }

    async #handleFormSubmission(response, formAction) {
        const status = response.status;

        if (status >= 500 && status < 600) {
            return this.#handleFetchError(response, formAction);
        }

        if (status === 422) {
            const text = await response.text();
            this.#updateModalContent(text, formAction);
            return;
        }

        if (status === 200 || status === 201) {
            const targetURL = this.#getTargetURL(response, formAction);
            this.#navigateToURL(targetURL, response);
            return;
        }

        throw new Error('Something went wrong');
    }

    async #submitForm(formElement, submitter) {
        const clickedButtons = this.#getSubmitButtons(submitter);

        try {
            const response = await this.fetch(formElement.action, {
                cache: 'no-cache',
                body: new FormData(formElement, clickedButtons[0]),
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            await this.#handleFormSubmission(response, formElement.action);
        } catch (error) {
            console.error(error);
        }
    }

    #executeScripts(container, scripts) {
        for (const script of scripts) {
            const newScript = this.document.createElement('script');

            if (script.innerHTML !== '') {
                newScript.innerHTML = `(function() { ${script.innerHTML} })()`;
            }

            for (const attr of ['type', 'async', 'defer', 'src', 'integrity']) {
                if ((script[attr] || '') !== '') {
                    newScript[attr] = script[attr];
                }
            }

            newScript.setAttribute('data-unique-id', `autowise-${Math.random().toString(18).substring(2)}`);
            container.appendChild(newScript);
        }
    }

    #updateModalBody(body, main, href) {
        body.innerHTML = main.innerHTML;

        const formElement = body.querySelector('form');

        if (formElement) {
            formElement.action = href;
            this.#formProtection = this.bindToForm(formElement);
        }

        this.#executeScripts(body, main.querySelectorAll('script'));
    }

    #dispatchContentUpdatedEvent() {
        this.window.dispatchEvent(
            new CustomEvent('AutoWise.Admin.ModalContentUpdated', {
                detail: { modal: this.modal },
            }),
        );
    }

    #updateModalContent(response, href) {
        const body = this.modal.querySelector('[data-ajax-modal-body]');
        const header = this.modal.querySelector('[data-ajax-modal-header]');
        const parser = new DOMParser();
        const htmlResponse = parser.parseFromString(response, 'text/html');
        const main = htmlResponse.querySelector('main');

        if (main === null) {
            return;
        }

        const h1 = htmlResponse.querySelector('h1');
        header.innerHTML = h1.innerHTML;
        this.#updateModalBody(body, main, href);

        this.document.querySelector('body').click();
        this.#dispatchContentUpdatedEvent();
    }

    static #handleClick = async (e) => {
        const target = e.target;

        const showButton = target.closest('[data-dialog-show]');
        if (showButton) {
            e.preventDefault();
            const modalId = showButton.dataset.dialogShow;
            const instance = AjaxModal.#getInstance(modalId);

            if (showButton.dataset.ajaxModalPost !== undefined) {
                const form = showButton.closest('form');
                const href = showButton.getAttribute('formaction') || showButton.href;

                showButton.disabled = true;
                try {
                    if (form !== null && typeof form.ajaxModalPrepare === 'function') {
                        await form.ajaxModalPrepare();
                    }

                    await instance.loadContentViaPost(href, new FormData(form));
                } finally {
                    showButton.disabled = false;
                }
            } else {
                const href = showButton.href || showButton.dataset.href;
                instance.loadContent(href);
            }
            return;
        }

        const closeButton = target.closest('[data-dialog-close]');
        if (closeButton) {
            const modal = closeButton.closest('[data-ajax-modal]');
            const instance = AjaxModal.#getInstance(modal.id);
            if (!instance.close()) {
                e.stopImmediatePropagation();
            }
            return;
        }

        if (target.id === 'remove_cancel' || 'closeButton' in target.dataset) {
            const modal = target.offsetParent?.querySelector('[data-dialog-close]');
            if (!modal) return;

            const ajaxModal = modal.closest('[data-ajax-modal]');
            if (!ajaxModal) return;

            const instance = AjaxModal.#getInstance(ajaxModal.id);
            if (instance && !instance.close()) {
                e.stopPropagation();
            }
            return;
        }
    };

    static #handleSubmit = (e) => {
        const form = e.target;
        const modal = form.closest('[data-ajax-modal]');
        if (!modal) return;

        e.preventDefault();

        const instance = AjaxModal.#getInstance(modal.id);
        instance.#submitForm(form, e.submitter);

        for (const button of modal.querySelectorAll('button[type="submit"], input[type=submit]')) {
            button.setAttribute('disabled', '');
            button.classList.add('cursor-not-allowed', 'opacity-50');
            button.innerHTML = `${loadingSpinnerHTML}${button.innerText}`;
        }
    };
}

AjaxModal.initialize(document.querySelectorAll('[data-ajax-modal]'));
